(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-kalista-infinite-scroll]';
  var CUSTOM_FILTER_PREFIX = 'new_fil_';

  function isCollectionStateParameter(key) {
    return key === 'sort_by' || key.indexOf('filter.') === 0 || key.indexOf(CUSTOM_FILTER_PREFIX) === 0;
  }

  function urlWithCurrentCollectionState(value) {
    var target = new URL(value, window.location.origin);
    var current = new URL(window.location.href);

    Array.from(target.searchParams.keys()).forEach(function (key) {
      if (isCollectionStateParameter(key)) target.searchParams.delete(key);
    });
    current.searchParams.forEach(function (itemValue, key) {
      if (isCollectionStateParameter(key)) target.searchParams.append(key, itemValue);
    });

    return target.origin === window.location.origin
      ? target.pathname + target.search + target.hash
      : target.toString();
  }

  function sortedCollectionUrl(sortValue) {
    var url = new URL(window.location.href);

    url.searchParams.set('sort_by', sortValue);
    // A sort change must restart the collection from its first page.
    url.searchParams.delete('page');

    return url.pathname + url.search + url.hash;
  }

  // The theme's built-in sort handler currently throws before navigation. Handle
  // collection sort links first while retaining the active filter query string.
  document.addEventListener('click', function (event) {
    var target = event.target;
    var sortLink = target && target.closest ? target.closest('a[data-sort-link]') : null;
    if (!sortLink) return;

    var sortValue = sortLink.getAttribute('data-value');
    if (!sortValue) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    var nextUrl = sortedCollectionUrl(sortValue);
    var currentUrl = window.location.pathname + window.location.search + window.location.hash;
    if (nextUrl !== currentUrl) window.location.assign(nextUrl);
  }, true);

  function hasUrlFilters() {
    return Array.from(new URL(window.location.href).searchParams.keys()).some(function (key) {
      return key.indexOf(CUSTOM_FILTER_PREFIX) === 0;
    });
  }

  function hasActiveFilters(section) {
    return hasUrlFilters() || Boolean(section.querySelector('[data-kalista-new-filter-root] input[data-filter-key]:checked'));
  }

  function unveilImages(container) {
    if (!window.lazySizes || !window.lazySizes.loader) return;
    container.querySelectorAll('.lazyload').forEach(function (element) {
      window.lazySizes.loader.unveil(element);
    });
  }

  function InfiniteCollection(root) {
    this.root = root;
    this.section = root.closest('[data-section-type="collection"]');
    this.grid = this.section && this.section.querySelector('[data-products-grid] .js-grid');
    this.startUrl = root.getAttribute('data-next-url') || '';
    if (this.startUrl) this.startUrl = urlWithCurrentCollectionState(this.startUrl);
    this.nextUrl = this.startUrl;
    this.controller = null;
    this.loading = false;
    this.loadedUrls = {};
    this.observer = null;

    if (!this.section || !this.grid || !this.nextUrl) return;
    this.bind();
    this.resume();
  }

  InfiniteCollection.prototype.bind = function () {
    var instance = this;
    var filterRoot = this.section.querySelector('[data-kalista-new-filter-root]');

    this.root.addEventListener('click', function (event) {
      if (!event.target.closest('[data-infinite-retry]')) return;
      event.preventDefault();
      instance.load();
    });

    if (filterRoot) {
      filterRoot.addEventListener('change', function () {
        if (hasActiveFilters(instance.section)) instance.pause();
      });
    }

    this.section.addEventListener('kalista:filters:rendered', function (event) {
      var activeCount = event.detail && Number(event.detail.activeCount || 0);
      if (activeCount > 0) {
        instance.pause();
      } else {
        instance.reset();
      }
    });
  };

  InfiniteCollection.prototype.observe = function () {
    var instance = this;
    if (this.observer || !this.nextUrl || hasActiveFilters(this.section)) return;
    this.observer = new IntersectionObserver(function (entries) {
      if (entries.some(function (entry) { return entry.isIntersecting; })) {
        instance.load();
      }
    }, { rootMargin: '800px 0px', threshold: 0.01 });
    this.observer.observe(this.root);
  };

  InfiniteCollection.prototype.disconnect = function () {
    if (!this.observer) return;
    this.observer.disconnect();
    this.observer = null;
  };

  InfiniteCollection.prototype.pause = function () {
    this.disconnect();
    if (this.controller) this.controller.abort();
    this.controller = null;
    this.loading = false;
    this.root.hidden = true;
    this.root.setAttribute('data-state', 'idle');
  };

  InfiniteCollection.prototype.resume = function () {
    if (!this.nextUrl || hasActiveFilters(this.section)) return;
    this.root.hidden = false;
    this.root.setAttribute('data-state', 'idle');
    this.observe();
  };

  InfiniteCollection.prototype.reset = function () {
    if (this.controller) this.controller.abort();
    this.controller = null;
    this.loading = false;
    this.loadedUrls = {};
    this.nextUrl = this.startUrl ? urlWithCurrentCollectionState(this.startUrl) : '';
    this.resume();
  };

  InfiniteCollection.prototype.finish = function () {
    this.disconnect();
    this.nextUrl = '';
    this.root.remove();
  };

  InfiniteCollection.prototype.fail = function () {
    this.loading = false;
    this.controller = null;
    this.root.hidden = false;
    this.root.setAttribute('data-state', 'error');
    var message = this.root.querySelector('[data-infinite-message]');
    if (message) message.textContent = 'Products could not be loaded.';
  };

  InfiniteCollection.prototype.load = function () {
    var instance = this;
    var requestedUrl = this.nextUrl ? urlWithCurrentCollectionState(this.nextUrl) : '';
    if (!requestedUrl || this.loading || this.loadedUrls[requestedUrl] || hasActiveFilters(this.section)) return;

    this.loading = true;
    this.disconnect();
    this.root.hidden = false;
    this.root.setAttribute('data-state', 'loading');
    var message = this.root.querySelector('[data-infinite-message]');
    if (message) message.textContent = 'Loading more products';
    this.controller = new AbortController();

    fetch(requestedUrl, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: this.controller.signal
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Collection page request failed');
        return response.text();
      })
      .then(function (html) {
        if (hasActiveFilters(instance.section)) {
          instance.pause();
          return;
        }

        var documentCopy = new DOMParser().parseFromString(html, 'text/html');
        var nextSection = documentCopy.querySelector('[data-section-id="' + instance.section.getAttribute('data-section-id') + '"][data-section-type="collection"]')
          || documentCopy.querySelector('[data-section-type="collection"]');
        var nextGrid = nextSection && nextSection.querySelector('[data-products-grid] .js-grid');
        if (!nextGrid) throw new Error('Next collection grid was not found');

        var fragment = document.createDocumentFragment();
        var appended = [];
        Array.prototype.forEach.call(nextGrid.children, function (child) {
          var imported = document.importNode(child, true);
          imported.classList.add('kalista-infinite-scroll__item');
          fragment.appendChild(imported);
          appended.push(imported);
        });
        if (!appended.length) {
          instance.finish();
          return;
        }

        instance.grid.appendChild(fragment);
        appended.forEach(unveilImages);
        instance.loadedUrls[requestedUrl] = true;

        var nextRoot = nextSection.querySelector(ROOT_SELECTOR);
        instance.nextUrl = nextRoot ? nextRoot.getAttribute('data-next-url') || '' : '';
        if (instance.nextUrl) instance.nextUrl = urlWithCurrentCollectionState(instance.nextUrl);
        instance.loading = false;
        instance.controller = null;
        instance.root.setAttribute('data-state', 'idle');

        document.dispatchEvent(new CustomEvent('theme:collection:append', {
          bubbles: true,
          detail: { container: instance.grid, items: appended }
        }));

        if (instance.nextUrl) {
          instance.observe();
        } else {
          instance.finish();
        }
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        console.warn('[Kalista infinite scroll]', error);
        instance.fail();
      });
  };

  function init(container) {
    container = container || document;
    var roots = [];
    if (container.nodeType === 1 && container.matches(ROOT_SELECTOR)) roots.push(container);
    if (container.querySelectorAll) roots = roots.concat(Array.from(container.querySelectorAll(ROOT_SELECTOR)));

    roots.forEach(function (root) {
      if (root.dataset.infiniteReady === 'true') return;
      root.dataset.infiniteReady = 'true';
      new InfiniteCollection(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      Array.prototype.forEach.call(mutation.addedNodes, function (node) {
        if (node.nodeType === 1) init(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
